
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tier1Tracking } from '@/services/analytics/Tier1Tracking';

export interface Tag {
  id: string;
  name: string;
  color: string;
  description?: string;
}

export const useTags = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all tags for the current user
  const { data: tags = [], isLoading: isLoadingTags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');
      
      if (error) {
        console.error('Error fetching tags:', error);
        return [];
      }
      
      // Map the raw data to our Tag interface, ensuring color exists
      return data.map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color || '#8E9196', // Default color if none is specified
        description: tag.description || ''
      }));
    },
    enabled: !!user,
  });

  // Create a new tag
  const createTag = useCallback(async (name: string, color: string = '#8E9196', description?: string) => {
    if (!user) return null;
    if (!name.trim()) {
      toast.error('Tag name cannot be empty');
      return null;
    }

    setIsLoading(true);
    console.log('[useTags Hook] === STARTING TAG CREATION PROCESS ===');
    console.log('[useTags Hook] User ID:', user.id);
    console.log('[useTags Hook] Tag data:', { name: name.trim(), color, description });
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert([{ 
          name: name.trim(), 
          color, 
          description: description?.trim() || '',
          user_id: user.id 
        }])
        .select()
        .single();
      
      if (error) {
        console.error('[useTags Hook] Error creating tag:', error);
        toast.error('Failed to create tag');
        return null;
      }
      
      console.log('[useTags Hook] === TAG CREATED SUCCESSFULLY IN DATABASE ===');
      console.log('[useTags Hook] New tag data:', data);
      
      // Create the Tag object from the response
      const newTag: Tag = {
        id: data.id,
        name: data.name,
        color: data.color || '#8E9196', // Ensure color exists
        description: data.description || ''
      };
      
      // CRITICAL: Track tag creation with Tier 1 analytics
      console.log('[useTags Hook] === STARTING ANALYTICS TRACKING ===');
      console.log('[useTags Hook] About to call Tier1Tracking.trackTagCreated...');
      console.log('[useTags Hook] Tracking parameters:', {
        tagId: newTag.id,
        name: newTag.name,
        color: newTag.color,
        userId: user.id,
        userEmail: user.email
      });
      
      try {
        await Tier1Tracking.trackTagCreated(
          newTag.id,
          newTag.name,
          newTag.color,
          user
        );
        console.log('[useTags Hook] === ANALYTICS TRACKING COMPLETED SUCCESSFULLY ===');
      } catch (trackingError) {
        console.error('[useTags Hook] === ANALYTICS TRACKING FAILED ===');
        console.error('[useTags Hook] Tracking error:', trackingError);
        console.error('[useTags Hook] Tracking error stack:', trackingError.stack);
        // Don't fail the whole operation if tracking fails
      }
      
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag created successfully');
      
      console.log('[useTags Hook] === TAG CREATION PROCESS COMPLETED ===');
      return newTag;
    } catch (error) {
      console.error('[useTags Hook] Error creating tag:', error);
      toast.error('Failed to create tag');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [user, queryClient]);

  // Update an existing tag
  const updateTag = useCallback(async (id: string, updates: { name?: string; color?: string; description?: string }) => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('tags')
        .update(updates)
        .eq('id', id);
      
      if (error) {
        console.error('Error updating tag:', error);
        toast.error('Failed to update tag');
        return false;
      }
      
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['tagContent', id] });
      toast.success('Tag updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating tag:', error);
      toast.error('Failed to update tag');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, queryClient]);

  // Delete a tag
  const deleteTag = useCallback(async (id: string) => {
    if (!user) return false;
    
    setIsLoading(true);
    try {
      // First delete all tag relationships
      const { error: relError } = await supabase
        .from('content_tags')
        .delete()
        .eq('tag_id', id);
      
      if (relError) {
        console.error('Error removing tag relationships:', relError);
        // Continue anyway to try to delete the tag
      }
      
      // Now delete the tag itself
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting tag:', error);
        toast.error('Failed to delete tag');
        return false;
      }
      
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      toast.success('Tag deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user, queryClient]);

  return {
    tags,
    isLoading: isLoading || isLoadingTags,
    createTag,
    updateTag,
    deleteTag
  };
};
